import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { TenantContactType } from "generated-graphql/graphql";
import { useFulfillmentDetails } from "./useFulfillmentDetails";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";

export function TenantContacts() {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();

  const contacts = data?.tierIIReport?.tenant?.contacts ?? [];

  const csm = contacts.find((c) => c.type === TenantContactType.Csm);
  const poc = contacts.find((c) => c.type === TenantContactType.CustomerPoc);
  const am = contacts.find((c) => c.type === TenantContactType.AccountManager);
  const wasteManager = contacts.find(
    (c) => c.type === TenantContactType.WasteManager
  );
  const dataManager = contacts.find(
    (c) => c.type === TenantContactType.DataManager
  );

  const hasAnyContact = csm || poc || am || wasteManager || dataManager;

  if (!hasAnyContact) {
    return null;
  }

  const formatName = (firstName?: string | null, lastName?: string | null) => {
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  };

  const ContactDisplay = ({
    label,
    contact,
  }: {
    label: string;
    contact?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      user?: {
        person?: {
          first?: string | null;
          last?: string | null;
        } | null;
      } | null;
    };
  }) => {
    if (!contact) return null;

    // Staff contacts (CSM, AM, DM, WM) get names from linked Person
    // POC gets names from TenantContact (Salesforce data)
    // Falls back to email if no name available
    const firstName = contact.user?.person?.first ?? contact.firstName;
    const lastName = contact.user?.person?.last ?? contact.lastName;
    const fullName = formatName(firstName, lastName);
    const name = fullName ?? contact.email;
    // Don't show email separately if it's already being used as the name
    const showEmailSeparately = fullName && contact.email;

    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {name && (
          <Typography variant="body2" fontWeight={500}>
            {name}
          </Typography>
        )}
        {showEmailSeparately && (
          <Typography variant="body2" color="text.secondary">
            {contact.email}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }} variant="outlined">
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 2, textTransform: "uppercase", letterSpacing: 1 }}
      >
        Account Contacts
      </Typography>
      <Stack spacing={2}>
        <ContactDisplay label="CSM (Customer Success Manager)" contact={csm} />
        <ContactDisplay label="Customer POC" contact={poc} />
        <ContactDisplay label="Account Manager" contact={am} />
        <ContactDisplay label="Data Manager" contact={dataManager} />
        <ContactDisplay label="Waste Manager" contact={wasteManager} />
      </Stack>
    </Paper>
  );
}
